import React, { useState, useEffect } from 'react';
import {
  getSoldTransactionById,
  clearSoldTransaction,
  authoriseSoldTransaction,
} from '../../actions/keyCodeActions';
import ReportedSoldTransactionDetails from './components/ReportedSoldTransactionDetails';
import ReportedSoldTransactionSkusList from './components/ReportedSoldTransactionSkusList';
import KeyProcessProgressBar from './components/KeyProcessProgressBar';
import ActionBar from '../layout/ActionBar';
import UploadSold from './slideout/UploadSold';
import KwPanel from '../common/KwPanel';
import { toast } from 'react-toastify';
import SlidingPane from 'react-sliding-pane';
import Button from '../common/Button';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../../util/pageTitle';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks';
import {
  addToGroup,
  removeFromGroup,
} from '../../middleware/signalRMiddleware';
import { hasPermission } from '../auth/authUtils';
import permissionTypes from '../../constants/permissionTypes';

function ReportedSoldTransaction() {
  const { id } = useParams<{ id: string }>();
  const [, setPageTitle] = usePageTitle(['Reported sold', 'Keys']);
  const currentUser = useAppSelector((state) => state.auth);
  const soldTransaction = useAppSelector((state) => state.soldTransaction);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [transaction, setTransaction] = useState<any>();
  const [reload, setReload] = useState<any>();
  const [uploadSlideoutOpen, setUploadSlideoutOpen] = useState(false);

  useEffect(() => {
    if (id) {
      getSoldTransactionById(id)
        .then((d) => {
          setTransaction(d);
          setPageTitle([
            d.keyProviderName,
            'Reported sold transaction',
            'Keys',
          ]);
        })
        .catch(() => {
          navigate('/keys/reported-sold/');
          toast.error(t('Transaction not found'));
        });
    }
  }, [id, reload, t, setPageTitle, navigate]);

  useEffect(() => {
    if (
      transaction &&
      (transaction.status === 'Pending' ||
        transaction.status === 'Pending Authorisation')
    ) {
      const key = `sold:${transaction.id}`;
      dispatch(addToGroup(key));
      return () => {
        dispatch(removeFromGroup(key));
      };
    }
  }, [transaction, dispatch]);

  useEffect(() => {
    if (soldTransaction && soldTransaction.id === id) {
      setTransaction((prevState: any) => ({
        ...prevState,
        ...soldTransaction,
      }));
      return () => dispatch(clearSoldTransaction());
    }
  }, [soldTransaction, id, dispatch]);

  const handleAuthorise = (e: React.MouseEvent) => {
    (e as any).cancelBubble = true;
    if (!id) return;
    authoriseSoldTransaction(id)
      .then((authorised) =>
        setTransaction({
          ...transaction,
          ...(authorised as Record<string, unknown>),
        })
      )
      .catch((err: Error) => {
        toast.error(err.message || t('Failed to authorise transaction'));
      });
  };

  return (
    <>
      <ActionBar
        breadcrumb={[
          { link: '/keys/reported-sold', label: t('Reported sold') },
          { label: t('Sold transaction') },
        ]}
      >
        <Button
          className="btn btn-primary"
          onClick={() => setUploadSlideoutOpen(true)}
          text={t('Upload sold keys')}
        />
      </ActionBar>
      {transaction && (
        <>
          <ReportedSoldTransactionDetails transaction={transaction} />
          {transaction.success && (
            <ReportedSoldTransactionSkusList id={id} reload={reload} />
          )}
          {!transaction.isAuthorised && hasPermission(currentUser, permissionTypes.MANAGE_KEYS) ? (
            <KwPanel className="mb-4">
              <header className="mb-2">
                <h2>{t('Authorise transaction')}</h2>
              </header>
              <Button
                className="btn btn-primary"
                onClick={handleAuthorise}
                text={t('Authorise')}
              />
            </KwPanel>
          ) : (
            <KeyProcessProgressBar transaction={transaction} />
          )}
        </>
      )}

      <SlidingPane
        isOpen={uploadSlideoutOpen}
        hideHeader={true}
        from="right"
        className="side-panel"
        onRequestClose={() => setUploadSlideoutOpen(false)}
      >
        <UploadSold
          done={() => {
            setUploadSlideoutOpen(false);
            setReload(new Date());
          }}
          cancel={() => setUploadSlideoutOpen(false)}
        />
      </SlidingPane>
    </>
  );
}

export default ReportedSoldTransaction;
